import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { CertificateMechanicalValue, MechanicalProperty } from '../types';
import { getMechanicalValidationResults, getStatusSummary } from '../utils/statusHelpers';
import SpecWindow from './SpecWindow';
import { statusChipSx, tokens } from '../theme';

interface MechanicalValuesDisplayProps {
  mechanicalValues: CertificateMechanicalValue[];
  materialGradeMechanicalProperties: MechanicalProperty[];
}

function MechanicalValuesDisplay({
  mechanicalValues,
  materialGradeMechanicalProperties,
}: MechanicalValuesDisplayProps): React.ReactElement {
  const rows = getMechanicalValidationResults(
    mechanicalValues,
    materialGradeMechanicalProperties
  );
  const summary = getStatusSummary(rows);

  if (rows.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="text.secondary">Keine mechanischen Eigenschaften erfasst.</Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Mechanische Eigenschaften
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Sollbereich · Ist-Wert · {summary.ok} im Bereich
        {summary.error > 0 ? ` · ${summary.error} außerhalb` : ''}
        {summary.missing > 0 ? ` · ${summary.missing} nicht gemessen` : ''}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(168px, 1fr))',
          gap: 1.5,
        }}
      >
        {rows.map((row) => {
          const color =
            row.status === 'ok' ? tokens.ok : row.status === 'error' ? tokens.bad : tokens.muted;
          const unit = row.unit || '';
          return (
            <Box
              key={row.name}
              sx={{ border: `1px solid ${tokens.border}`, borderRadius: '10px', p: 1.5 }}
            >
              <Typography variant="body2" fontWeight={500}>
                {row.name}
              </Typography>
              <Typography sx={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.03em', color, my: 0.5 }}>
                {row.status === 'missing' ? '—' : row.measuredValue}
                {row.status !== 'missing' && unit && (
                  <Typography component="span" sx={{ fontSize: 12, fontWeight: 400, color: tokens.muted, ml: 0.5 }}>
                    {unit}
                  </Typography>
                )}
              </Typography>
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  fontSize: 11,
                  px: 1,
                  py: 0.15,
                  borderRadius: 999,
                  ...statusChipSx(row.status),
                }}
              >
                {row.statusMessage}
              </Box>
              <SpecWindow
                min={row.minValue}
                max={row.maxValue}
                value={row.status === 'missing' ? null : row.measuredValue}
                status={row.status}
                decimals={1}
                unit={unit}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default MechanicalValuesDisplay;
